import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Key, Car, Settings, Shield, Clock, Phone } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Services = ({ currentLang, translations }) => {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();
  const [emergencyRef, emergencyVisible] = useScrollAnimation();
  
  const services = [
    {
      icon: Key,
      key: 'carKeys',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Settings,
      key: 'ignition',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: Car,
      key: 'lockout',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: Shield,
      key: 'transponder',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`max-w-4xl mx-auto text-center mb-16 scroll-animate ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {translations[currentLang].services.title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {translations[currentLang].services.subtitle}
          </p>
        </div>

        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 ${gridVisible ? 'visible' : ''}`}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card 
                key={service.key} 
                className={`group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 transform hover:scale-105 scroll-animate stagger-${index + 1} ${gridVisible ? 'visible' : ''} h-full flex flex-col`}
              >
                <CardContent className="p-8 text-center flex flex-col flex-grow">
                  <div className={`w-16 h-16 ${service.bgColor} rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform mb-4`}>
                    <IconComponent className={`w-8 h-8 ${service.color}`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {translations[currentLang].services.items[service.key].title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                    {translations[currentLang].services.items[service.key].description}
                  </p>
                  
                  <div className="mt-auto">
                    <Button 
                      variant="outline" 
                      className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all"
                      onClick={() => window.open('tel:+972553002025')}
                    >
                      {translations[currentLang].services.getService}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Emergency Section */}
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-8 md:p-12 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Clock className="w-8 h-8 text-red-600" />
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                {translations[currentLang].services.emergency.title}
              </h3>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              {translations[currentLang].services.emergency.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg flex items-center space-x-3 shadow-lg hover:shadow-xl transition-all"
                onClick={() => window.open('tel:+972553002025')}
              >
                <Phone className="w-5 h-5" />
                <span>{translations[currentLang].services.emergency.callNow}</span>
              </Button>
              
              <div className="text-2xl font-bold text-red-600">
                +972-55-300-2025
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;